import { query, queryAll } from '../core/dom.js';
import ScrollTrigger from 'gsap/ScrollTrigger';

const SELECTORS = {
  slider: '[data-single-product-slider]',
  track: '[data-single-product-slider-track]',
  slide: '.product-content__slide',
  tab: '[data-single-product-slider-tab]',
  prev: '[data-single-product-slider-prev]',
  next: '[data-single-product-slider-next]',
  inner: '.product-content__inner',
  sampleForm: '[data-product-sample-form]',
};

const syncSampleRequestHiddenFields = () => {
  const sampleForm = query(SELECTORS.sampleForm);
  if (!sampleForm) {
    return;
  }

  const productName = sampleForm.dataset.productName?.trim() ?? '';
  const productCategory = sampleForm.dataset.productCategory?.trim() ?? '';

  const setHiddenValues = () => {
    const forminatorForm =
      query('form[id^="forminator-module-152"]', sampleForm) ||
      query('form.forminator-custom-form', sampleForm);

    if (!forminatorForm) {
      return false;
    }

    const productField = query('input[name="hidden-1"]', forminatorForm);
    const categoryField = query('input[name="hidden-2"]', forminatorForm);

    if (productField) {
      productField.value = productName;
    }

    if (categoryField) {
      categoryField.value = productCategory;
    }

    if (forminatorForm.dataset.hiddenFieldsSyncBound !== 'true') {
      forminatorForm.dataset.hiddenFieldsSyncBound = 'true';
      forminatorForm.addEventListener('submit', () => {
        const submitProductField = query('input[name="hidden-1"]', forminatorForm);
        const submitCategoryField = query('input[name="hidden-2"]', forminatorForm);

        if (submitProductField) {
          submitProductField.value = productName;
        }
        if (submitCategoryField) {
          submitCategoryField.value = productCategory;
        }
      });
    }

    return Boolean(productField || categoryField);
  };

  if (setHiddenValues()) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (setHiddenValues()) {
      observer.disconnect();
    }
  });

  observer.observe(sampleForm, { childList: true, subtree: true });
};
export function initSingleProductSlider() {
  syncSampleRequestHiddenFields();

  const slider = query(SELECTORS.slider);

  if (!slider) {
    return;
  }

  const track = query(SELECTORS.track, slider);
  const slides = queryAll(SELECTORS.slide, track);
  const tabs = queryAll(SELECTORS.tab, slider);
  const prevButton = query(SELECTORS.prev, slider);
  const nextButton = query(SELECTORS.next, slider);

  if (!track || !tabs.length || !slides.length) {
    return;
  }

  const slidesByGroup = slides.reduce((groups, slide, index) => {
    const group = slide.dataset.slideGroup;
    if (!group) {
      return groups;
    }
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(index);
    return groups;
  }, {});

  let activeGroup = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.slideGroup;
  if (!activeGroup || !slidesByGroup[activeGroup]?.length) {
    activeGroup = Object.keys(slidesByGroup)[0];
  }

  if (!activeGroup) {
    return;
  }

  let activeIndex = slidesByGroup[activeGroup][0] ?? 0;

  const updateState = (index, group) => {
    const offset = `-${index * 100}%`;
    track.style.transform = `translateX(${offset})`;

    slides.forEach((slide, slideIndex) => {
      const isActive = index === slideIndex;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    tabs.forEach((tab) => {
      const isActive = group === tab.dataset.slideGroup;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  };

  const clampIndex = (value, group) => {
    const groupIndexes = slidesByGroup[group] ?? [];
    if (!groupIndexes.length) {
      return value;
    }

    const min = groupIndexes[0];
    const max = groupIndexes[groupIndexes.length - 1];
    return Math.min(Math.max(value, min), max);
  };

  const goToSlide = (index, group = activeGroup) => {
    const nextIndex = clampIndex(index, group);

    if (nextIndex === activeIndex && group === activeGroup) {
      return;
    }

    activeGroup = group;
    activeIndex = nextIndex;
    updateState(activeIndex, activeGroup);
  };

  prevButton?.addEventListener('click', () => goToSlide(activeIndex - 1, activeGroup));
  nextButton?.addEventListener('click', () => goToSlide(activeIndex + 1, activeGroup));
  tabs.forEach((tab) =>
    tab.addEventListener('click', () => {
      const targetGroup = tab.dataset.slideGroup;
      const targetIndex = slidesByGroup[targetGroup]?.[0];
      if (!targetGroup || targetIndex === undefined) {
        return;
      }
      goToSlide(targetIndex, targetGroup);
    }),
  );

  updateState(activeIndex, activeGroup);

  const inner = slider.closest(SELECTORS.inner);
  const shouldPin = window.matchMedia('(min-width: 1025px)').matches;

  if (!inner || !shouldPin) {
    return;
  }

  ScrollTrigger.create({
    trigger: inner,
    start: 'top top',
    end: () => `bottom top+=${slider.offsetHeight}`,
    pin: slider,
    pinSpacing: false,
    invalidateOnRefresh: true,
  });
}
